class LiveSession {
  constructor(store) {
    // this._wss = "wss://live.clocktower.online:8080/";
    // Use current hostname for WebSocket connection in development
    const protocol = window.location.protocol === "https:" ? "wss://" : "ws://";
    const hostname = window.location.hostname;
    
    // In production (non-localhost/LAN), use Nginx reverse proxy path /ws
    // In development (localhost or LAN), connect directly to port 8081
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.match(/^192\.168\.\d+\.\d+$/)) {
      this._wss = `${protocol}${hostname}:8081/`;
    } else {
      // Production: use /ws path with same host (Nginx will proxy to backend)
      this._wss = `${protocol}${hostname}/ws/`;
    }
    
    console.log("[WebSocket] Configured WebSocket URL:", this._wss);
    console.log("[WebSocket] Protocol:", protocol, "Hostname:", hostname);
    this._socket = null;
    this._isSpectator = true;
    this._gamestate = [];
    this._store = store;
    this._pingInterval = 30 * 1000; // 30 seconds between pings
    this._pingTimeout = 60 * 60 * 1000; // 1 hour timeout - accommodate mobile screen sleep and breaks
    this._pingTimer = null;
    this._reconnectTimer = null;
    this._heartbeatTimer = null;
    this._lastHeartbeatResponse = Date.now();
    this._players = {}; // map of players connected to a session
    this._pings = {}; // map of player IDs to ping
    this._justConnected = false; // track if ST just connected to avoid clearing seats
    
    // Setup Page Visibility API to handle mobile screen sleep
    this._setupVisibilityHandler();
    
    // reconnect to previous session
    if (this._store.state.session.sessionId) {
      console.log("[WebSocket] Auto-reconnecting to previous session:", this._store.state.session.sessionId);
      this.connect(this._store.state.session.sessionId);
    }
  }

  /**
   * Setup visibility change handler for mobile screen sleep/wake
   * @private
   */
  _setupVisibilityHandler() {
    this._hiddenAt = null;
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        console.log("[Visibility] Page became visible");

        const sessionId = this._store.state.session.sessionId;
        if (!sessionId) {
          console.log("[Visibility] No active session to reconnect");
          return;
        }

        // Skip reconnect for brief switches (< 5s) to avoid screenshot tool disconnects
        const hiddenDuration = this._hiddenAt ? Date.now() - this._hiddenAt : 0;
        if (hiddenDuration < 5000) {
          console.log("[Visibility] Brief switch (" + hiddenDuration + "ms), skipping reconnect");
          return;
        }

        // Only reconnect if socket is actually dead or unhealthy
        if (!this._socket || this._socket.readyState !== 1) {
          console.log("[Visibility] WebSocket disconnected, reconnecting...");
          if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
          }
          this.connect(sessionId);
        } else {
          console.log("[Visibility] WebSocket connected, requesting fresh data");
          this._ping();
          if (this._isSpectator) {
            this._sendDirect("host", "getGamestate", this._store.state.session.playerId);
          }
          this.requestChatHistory();
          this._fetchMailbox(sessionId);
        }
      } else if (document.hidden) {
        console.log("[Visibility] Page hidden (screen sleep)");
        this._hiddenAt = Date.now();
      }
    });
  }

  /**
   * Fetch mailbox via HTTP for immediate message recovery on wake.
   * Runs in parallel with WebSocket reconnection.
   * @param channel
   * @private
   */
  _fetchMailbox(channel) {
    const playerId = this._isSpectator
      ? this._store.state.session.playerId
      : "host";
    const apiUrl = this._getMailboxUrl(channel, playerId);

    console.log("[Mailbox] Fetching:", apiUrl);

    fetch(apiUrl)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        console.log("[Mailbox] Received:", data);
        this._applyMailbox(data);
      })
      .catch(err => {
        console.warn("[Mailbox] HTTP fetch failed, falling back to WS:", err.message);
      });
  }

  /**
   * Build the mailbox API URL based on current environment.
   * @param channel
   * @param playerId
   * @returns {string}
   * @private
   */
  _getMailboxUrl(channel, playerId) {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.match(/^192\.168\.\d+\.\d+$/)) {
      return `http://${hostname}:8082/api/mailbox/${channel}/${playerId}`;
    }
    return `${window.location.protocol}//${hostname}/api/mailbox/${channel}/${playerId}`;
  }

  /**
   * Apply mailbox data (role, bluffs, chat, screenshots) from HTTP response.
   * @param data { role, bluffs, chat, screenshots }
   * @private
   */
  _applyMailbox(data) {
    const { role, bluffs, chat, screenshots } = data;

    // Apply role if present
    if (role && this._isSpectator) {
      const playerId = this._store.state.session.playerId;
      const players = this._store.state.players.players;
      const playerIndex = players.findIndex((p) => p.id === playerId);
      if (playerIndex >= 0) {
        const player = players[playerIndex];
        if (player.role.id !== role) {
          const roleObj =
            this._store.state.roles.get(role) ||
            this._store.getters.rolesJSONbyId.get(role) ||
            {};
          if (roleObj.id) {
            console.log("[Mailbox] Applying role:", role);
            this._store.commit("players/update", {
              player,
              property: "role",
              value: roleObj,
            });
          }
        }
      }
    }

    // Apply bluffs if present
    if (bluffs && bluffs.length > 0 && this._isSpectator) {
      console.log("[Mailbox] Applying bluffs:", bluffs);
      const roles = this._store.state.roles;
      bluffs.forEach((id, index) => {
        const bluffRole = id ? roles.get(id) : null;
        this._store.commit("players/setBluff", { index, role: bluffRole || {} });
      });
    }

    // Apply chat with deduplication
    if (chat && Object.keys(chat).length > 0) {
      console.log("[Mailbox] Applying chat messages");
      this._applyChatFromMailbox(chat);
    }

    // Apply screenshots from mailbox
    if (screenshots && Object.keys(screenshots).length > 0 && this._isSpectator) {
      console.log("[Mailbox] Applying screenshots:", Object.keys(screenshots).length);
      for (let msgId in screenshots) {
        const ss = screenshots[msgId];
        if (ss.expired) {
          this._store.commit("session/receiveScreenshot", {
            fromPlayerId: "storyteller",
            msgId,
            imageData: null,
            mode: ss.mode,
            ttl: ss.ttl,
            timestamp: ss.timestamp,
          });
          this._store.commit("session/screenshotExpired", { chatPlayerId: "storyteller", msgId });
        } else {
          this._store.commit("session/receiveScreenshot", {
            fromPlayerId: "storyteller",
            msgId,
            imageData: ss.imageData,
            mode: ss.mode,
            ttl: ss.ttl,
            timestamp: ss.timestamp,
            viewedAt: ss.viewedAt || null,
          });
          if (ss.viewedAt) {
            // Check if already expired based on server's viewedAt + ttl
            if (ss.mode === "timer" && ss.viewedAt + (ss.ttl * 1000) < Date.now()) {
              this._store.commit("session/screenshotExpired", { chatPlayerId: "storyteller", msgId });
            }
          }
        }
      }
    }
  }

  /**
   * Apply chat messages from mailbox with deduplication.
   * Deduplication is also handled in the Vuex mutation (receiveChatMessage),
   * so this is safe even if some messages already exist.
   * @param chat object with otherPlayerId -> messages array
   * @private
   */
  _applyChatFromMailbox(chat) {
    const players = this._store.state.players.players;

    for (let otherPlayerId in chat) {
      const messages = chat[otherPlayerId];
      messages.forEach(({ fromPlayerId, content, timestamp }) => {
        let senderName = "Unknown";
        if (fromPlayerId === "storyteller") {
          senderName = "Storyteller";
        } else {
          const player = players.find((p) => p.id === fromPlayerId);
          if (player) {
            const index = players.indexOf(player);
            const seatNumber = index + 1;
            const playerName =
              player.name === "__EMPTY_SEAT__" ? "Empty Seat" : player.name;
            senderName = `${seatNumber}. ${playerName}`;
          }
        }

        // receiveChatMessage mutation handles deduplication internally
        this._store.commit("session/receiveChatMessage", {
          fromPlayerId,
          senderName,
          content,
          timestamp,
        });
      });
    }
  }

  /**
   * Open a new session for the passed channel.
   * @param channel
   * @private
   */
  _open(channel) {
    console.log("[WebSocket] Opening connection for channel:", channel);
    console.log("[WebSocket] isSpectator:", this._isSpectator);
    console.log("[WebSocket] playerId:", this._store.state.session.playerId);
    
    this.disconnect();
    
    const wsUrl = this._wss + channel + "/" + (this._isSpectator ? this._store.state.session.playerId : "host");
    console.log("[WebSocket] Full WebSocket URL:", wsUrl);
    
    try {
      this._socket = new WebSocket(wsUrl);
      console.log("[WebSocket] WebSocket object created");
    } catch (error) {
      console.error("[WebSocket] Failed to create WebSocket:", error);
      return;
    }
    
    this._socket.addEventListener("message", this._handleMessage.bind(this));
    this._socket.onopen = this._onOpen.bind(this);
    this._socket.onerror = (error) => {
      console.error("[WebSocket] Connection error:", error);
      console.error("[WebSocket] Make sure server is running at:", this._wss);
    };
    this._socket.onclose = (err) => {
      console.log("[WebSocket] Connection closed. Code:", err.code, "Reason:", err.reason);
      this._socket = null;
      clearInterval(this._pingTimer);
      clearTimeout(this._heartbeatTimer);
      this._pingTimer = null;
      this._heartbeatTimer = null;
      if (err.code !== 1000) {
        // connection interrupted, reconnect after 3 seconds
        console.log("[WebSocket] Abnormal close, reconnecting in 3 seconds...");
        this._store.commit("session/setReconnecting", true);
        this._reconnectTimer = setTimeout(
          () => this.connect(channel),
          3 * 1000,
        );
      } else {
        console.log("[WebSocket] Normal close");
        this._store.commit("session/setSessionId", "");
        if (err.reason) {
          this._store.commit("setConfirmModal", {
            title: "连接已关闭",
            message: err.reason,
            confirmText: "确定",
            showCancelButton: false,
            onConfirm: () => {},
          });
        }
      }
    };
  }

  /**
   * Send a message through the socket.
   * @param command
   * @param params
   * @private
   */
  _send(command, params) {
    if (this._socket && this._socket.readyState === 1) {
      const message = JSON.stringify([command, params]);
      if (command === "screenshot") {
        console.log("[WebSocket] Sending screenshot, size:", message.length);
      } else {
        console.log("[WebSocket] Sending message:", command);
      }
      this._socket.send(message);
    } else {
      console.warn("[WebSocket] Cannot send message, socket not ready. State:", 
        this._socket ? this._socket.readyState : "null");
    }
  }

  /**
   * Send a message directly to a single playerId, if provided.
   * Otherwise broadcast it.
   * @param playerId player ID or "host", optional
   * @param command
   * @param params
   * @private
   */
  _sendDirect(playerId, command, params) {
    if (playerId) {
      this._send("direct", { [playerId]: [command, params] });
    } else {
      this._send(command, params);
    }
  }

  /**
   * Open event handler for socket.
   * @private
   */
  _onOpen() {
    console.log("[WebSocket] Connection opened successfully!");
    console.log("[WebSocket] isSpectator:", this._isSpectator);
    
    // Clear reconnecting state
    this._store.commit("session/setReconnecting", false);
    
    if (this._isSpectator) {
      console.log("[WebSocket] Spectator mode: requesting gamestate from host");
      this._sendDirect(
        "host",
        "getGamestate",
        this._store.state.session.playerId,
      );

      // Request chat history to sync missed messages
      console.log("[WebSocket] Requesting chat history");
      this.requestChatHistory();

      // Fetch mailbox to recover screenshots (imageData stripped from localStorage)
      const sessionId = this._store.state.session.sessionId;
      if (sessionId) {
        this._fetchMailbox(sessionId);
      }
    } else {
      console.log("[WebSocket] Host mode: sending gamestate to spectators");
      this._justConnected = true; // Mark as just connected to avoid clearing seats
      // Restore player connections from current game state
      const now = new Date().getTime();
      this._store.state.players.players.forEach((player) => {
        if (player.id) {
          this._players[player.id] = now;
        }
      });
      this.sendGamestate();
      
      // Host also requests chat history
      console.log("[WebSocket] Host requesting chat history");
      this.requestChatHistory();
    }
    this._ping();
    console.log("[WebSocket] Started ping interval");
  }

  /**
   * Send a ping message with player ID and ST flag.
   * @private
   */
  _ping() {
    this._handlePing();
    this._send("ping", [
      this._isSpectator
        ? this._store.state.session.playerId
        : Object.keys(this._players).length,
      "latency",
    ]);
    clearTimeout(this._pingTimer);
    this._pingTimer = setTimeout(this._ping.bind(this), this._pingInterval);
    
    // Start heartbeat check - if no response in 60 seconds, reconnect
    this._startHeartbeatCheck();
  }

  /**
   * Start heartbeat check to detect connection loss
   * @private
   */
  _startHeartbeatCheck() {
    clearTimeout(this._heartbeatTimer);
    this._heartbeatTimer = setTimeout(() => {
      const timeSinceLastHeartbeat = Date.now() - this._lastHeartbeatResponse;
      if (timeSinceLastHeartbeat > 60000) {
        console.log("[Heartbeat] No response for 60 seconds, reconnecting...");
        const sessionId = this._store.state.session.sessionId;
        if (sessionId && this._socket) {
          this._socket.close();
          this.connect(sessionId);
        }
      }
    }, 60000);
  }

  /**
   * Handle an incoming socket message.
   * @param data
   * @private
   */
  _handleMessage({ data }) {
    let command, params;
    try {
      [command, params] = JSON.parse(data);
    } catch (err) {
      console.log("unsupported socket message", data);
    }
    switch (command) {
      case "getGamestate":
        this.sendGamestate(params);
        break;
      case "edition":
        this._updateEdition(params);
        break;
      case "fabled":
        this._updateFabled(params);
        break;
      case "gs":
        this._updateGamestate(params);
        break;
      case "player":
        this._updatePlayer(params);
        break;
      case "claim":
        this._updateSeat(params);
        break;
      case "ping":
        this._handlePing(params);
        break;
      case "nomination":
        if (!this._isSpectator) return;
        if (!params) {
          // create vote history record
          this._store.commit(
            "session/addHistory",
            this._store.state.players.players,
          );
        }
        this._store.commit("session/nomination", { nomination: params });
        break;
      case "swap":
        if (!this._isSpectator) return;
        this._store.commit("players/swap", params);
        break;
      case "move":
        if (!this._isSpectator) return;
        this._store.commit("players/move", params);
        break;
      case "remove":
        if (!this._isSpectator) return;
        this._store.commit("players/remove", params);
        break;
      case "marked":
        if (!this._isSpectator) return;
        this._store.commit("session/setMarkedPlayer", params);
        break;
      case "isNight":
        if (!this._isSpectator) return;
        this._store.commit("toggleNight", params);
        break;
      case "isVoteHistoryAllowed":
        if (!this._isSpectator) return;
        this._store.commit("session/setVoteHistoryAllowed", params);
        this._store.commit("session/clearVoteHistory");
        break;
      case "votingSpeed":
        if (!this._isSpectator) return;
        this._store.commit("session/setVotingSpeed", params);
        break;
      case "clearVoteHistory":
        if (!this._isSpectator) return;
        this._store.commit("session/clearVoteHistory");
        break;
      case "isVoteInProgress":
        if (!this._isSpectator) return;
        this._store.commit("session/setVoteInProgress", params);
        break;
      case "vote":
        this._handleVote(params);
        break;
      case "lock":
        this._handleLock(params);
        break;
      case "clearRoles":
        if (!this._isSpectator) return;
        console.log("[WebSocket] Received clearRoles command, clearing all roles");
        this._store.dispatch("players/clearRoles");
        break;
      case "bye":
        this._handleBye(params);
        break;
      case "pronouns":
        this._updatePlayerPronouns(params);
        break;
      case "chat":
        this._handleChatMessage(params);
        break;
      case "chatHistory":
        this._handleChatHistory(params);
        break;
      case "updateNickname":
        this._handleNicknameUpdate(params);
        break;
      case "bluffs":
        this._handleBluffs(params);
        break;
      case "screenshot":
        this._handleScreenshot(params);
        break;
      case "screenshotRevoked":
        this._handleScreenshotRevoked(params);
        break;
    }
  }

  /**
   * Connect to a new live session, either as host or spectator.
   * Set a unique playerId if there isn't one yet.
   * @param channel
   */
  connect(channel) {
    console.log("[WebSocket] connect() called with channel:", channel);
    if (!this._store.state.session.playerId) {
      const newPlayerId = Math.random().toString(36).substr(2);
      console.log("[WebSocket] Generated new playerId:", newPlayerId);
      this._store.commit("session/setPlayerId", newPlayerId);
    } else {
      console.log("[WebSocket] Using existing playerId:", this._store.state.session.playerId);
    }
    this._pings = {};
    this._store.commit("session/setPlayerCount", 0);
    this._store.commit("session/setPing", 0);
    this._isSpectator = this._store.state.session.isSpectator;
    console.log("[WebSocket] isSpectator set to:", this._isSpectator);
    this._open(channel);
  }

  /**
   * Close the current session, if any.
   */
  disconnect() {
    this._pings = {};
    this._store.commit("session/setPlayerCount", 0);
    this._store.commit("session/setPing", 0);
    this._store.commit("session/setReconnecting", false);
    clearTimeout(this._reconnectTimer);
    clearTimeout(this._pingTimer);
    clearTimeout(this._heartbeatTimer);
    this._reconnectTimer = null;
    this._pingTimer = null;
    this._heartbeatTimer = null;
    if (this._socket) {
      if (this._isSpectator) {
        this._sendDirect("host", "bye", this._store.state.session.playerId);
      }
      this._socket.close(1000);
      this._socket = null;
    }
  }

  /**
   * Publish the current gamestate.
   * Optional param to reduce traffic. (send only player data)
   * @param playerId
   * @param isLightweight
   */
  sendGamestate(playerId = "", isLightweight = false) {
    if (this._isSpectator) return;
    this._gamestate = this._store.state.players.players.map((player) => ({
      name: player.name,
      id: player.id,
      isDead: player.isDead,
      isVoteless: player.isVoteless,
      pronouns: player.pronouns,
      ...(player.role && player.role.team === "traveler"
        ? { roleId: player.role.id }
        : {}),
    }));
    if (isLightweight) {
      this._sendDirect(playerId, "gs", {
        gamestate: this._gamestate,
        isLightweight,
      });
    } else {
      const { session, grimoire } = this._store.state;
      const { fabled } = this._store.state.players;
      this.sendEdition(playerId);
      this._sendDirect(playerId, "gs", {
        gamestate: this._gamestate,
        isNight: grimoire.isNight,
        isVoteHistoryAllowed: session.isVoteHistoryAllowed,
        nomination: session.nomination,
        votingSpeed: session.votingSpeed,
        lockedVote: session.lockedVote,
        isVoteInProgress: session.isVoteInProgress,
        markedPlayer: session.markedPlayer,
        fabled: fabled.map((f) => (f.isCustom ? f : { id: f.id })),
        ...(session.nomination ? { votes: session.votes } : {}),
      });
    }
  }

  /**
   * Update the gamestate based on incoming data.
   * @param data
   * @private
   */
  _updateGamestate(data) {
    console.log("[WebSocket] _updateGamestate called");
    console.log("[WebSocket] Received gamestate data:", data);
    
    if (!this._isSpectator) {
      console.log("[WebSocket] Not a spectator, ignoring gamestate update");
      return;
    }
    
    const {
      gamestate,
      isLightweight,
      isNight,
      isVoteHistoryAllowed,
      nomination,
      votingSpeed,
      votes,
      lockedVote,
      isVoteInProgress,
      markedPlayer,
      fabled,
    } = data;
    
    console.log("[WebSocket] Gamestate array length:", gamestate ? gamestate.length : 0);
    console.log("[WebSocket] Current player count:", this._store.state.players.players.length);
    
    const players = this._store.state.players.players;
    // adjust number of players
    if (players.length < gamestate.length) {
      console.log("[WebSocket] Adding", gamestate.length - players.length, "players");
      for (let x = players.length; x < gamestate.length; x++) {
        this._store.commit("players/add", gamestate[x].name);
      }
    } else if (players.length > gamestate.length) {
      console.log("[WebSocket] Removing", players.length - gamestate.length, "players");
      for (let x = players.length; x > gamestate.length; x--) {
        this._store.commit("players/remove", x - 1);
      }
    }
    console.log("[WebSocket] Player count after adjustment:", this._store.state.players.players.length);
    // update status for each player
    gamestate.forEach((state, x) => {
      const player = players[x];
      const { roleId } = state;
      // update relevant properties
      ["name", "id", "isDead", "isVoteless", "pronouns"].forEach((property) => {
        const value = state[property];
        if (player[property] !== value) {
          this._store.commit("players/update", { player, property, value });
        }
      });
      // roles are special, because of travelers
      if (roleId && player.role.id !== roleId) {
        const role =
          this._store.state.roles.get(roleId) ||
          this._store.getters.rolesJSONbyId.get(roleId);
        if (role) {
          this._store.commit("players/update", {
            player,
            property: "role",
            value: role,
          });
        }
      } else if (!roleId && player.role.team === "traveler") {
        this._store.commit("players/update", {
          player,
          property: "role",
          value: {},
        });
      }
    });
    if (!isLightweight) {
      this._store.commit("toggleNight", !!isNight);
      this._store.commit("session/setVoteHistoryAllowed", isVoteHistoryAllowed);
      this._store.commit("session/nomination", {
        nomination,
        votes,
        votingSpeed,
        lockedVote,
        isVoteInProgress,
      });
      this._store.commit("session/setMarkedPlayer", markedPlayer);
      this._store.commit("players/setFabled", {
        fabled: fabled.map((f) => this._store.state.fabled.get(f.id) || f),
      });
    }
  }

  /**
   * Send clearRoles command to all players. ST only
   */
  sendClearRoles() {
    if (this._isSpectator) return;
    console.log("[WebSocket] Sending clearRoles to all players");
    this._send("clearRoles", true);
  }

  /**
   * Publish an edition update. ST only
   * @param playerId
   */
  sendEdition(playerId = "") {
    if (this._isSpectator) return;
    const { edition } = this._store.state;
    let roles;
    if (!edition.isOfficial) {
      roles = this._store.getters.customRolesStripped;
    }
    this._sendDirect(playerId, "edition", {
      edition: edition.isOfficial ? { id: edition.id } : edition,
      ...(roles ? { roles } : {}),
    });
  }

  /**
   * Update edition and roles for custom editions.
   * @param edition
   * @param roles
   * @private
   */
  _updateEdition({ edition, roles }) {
    if (!this._isSpectator) return;
    this._store.commit("setEdition", edition);
    if (roles) {
      this._store.commit("setCustomRoles", roles);
      if (this._store.state.roles.size !== roles.length) {
        const missing = [];
        roles.forEach(({ id }) => {
          if (!this._store.state.roles.get(id)) {
            missing.push(id);
          }
        });
        this._store.commit("setConfirmModal", {
          title: "缺少自定义角色",
          message: 
            "此会话包含找不到的自定义角色，请先加载这些角色再加入游戏！\n\n" +
            `缺少的角色：${missing.join(", ")}`,
          confirmText: "确定",
          showCancelButton: false,
          onConfirm: () => {},
        });
        this.disconnect();
        this._store.commit("toggleModal", "edition");
      }
    }
  }

  /**
   * Publish a fabled update. ST only
   */
  sendFabled() {
    if (this._isSpectator) return;
    const { fabled } = this._store.state.players;
    this._send(
      "fabled",
      fabled.map((f) => (f.isCustom ? f : { id: f.id })),
    );
  }

  /**
   * Update fabled roles.
   * @param fabled
   * @private
   */
  _updateFabled(fabled) {
    if (!this._isSpectator) return;
    this._store.commit("players/setFabled", {
      fabled: fabled.map((f) => this._store.state.fabled.get(f.id) || f),
    });
  }

  /**
   * Publish a player update.
   * @param player
   * @param property
   * @param value
   */
  sendPlayer({ player, property, value }) {
    if (this._isSpectator || property === "reminders") return;
    const index = this._store.state.players.players.indexOf(player);
    if (property === "role") {
      if (value.team && value.team === "traveler") {
        // update local gamestate to remember this player as a traveler
        this._gamestate[index].roleId = value.id;
        this._send("player", {
          index,
          property,
          value: value.id,
        });
      } else if (this._gamestate[index].roleId) {
        // player was previously a traveler
        delete this._gamestate[index].roleId;
        this._send("player", { index, property, value: "" });
      }
    } else {
      this._send("player", { index, property, value });
    }
  }

  /**
   * Update a player based on incoming data. Player only.
   * @param index
   * @param property
   * @param value
   * @private
   */
  _updatePlayer({ index, property, value }) {
    if (!this._isSpectator) return;
    const player = this._store.state.players.players[index];
    if (!player) return;
    // special case where a player stops being a traveler
    if (property === "role") {
      // Handle null or empty value - clear the role
      if (!value || value === null) {
        this._store.commit("players/update", {
          player,
          property: "role",
          value: {},
        });
      } else {
        // load role, first from session, the global, then fail gracefully
        const role =
          this._store.state.roles.get(value) ||
          this._store.getters.rolesJSONbyId.get(value) ||
          {};
        this._store.commit("players/update", {
          player,
          property: "role",
          value: role,
        });
      }
    } else {
      // just update the player otherwise
      this._store.commit("players/update", { player, property, value });
    }
  }

  /**
   * Publish a player pronouns update
   * @param player
   * @param value
   * @param isFromSockets
   */
  sendPlayerPronouns({ player, value, isFromSockets }) {
    //send pronoun only for the seated player or storyteller
    //Do not re-send pronoun data for an update that was recieved from the sockets layer
    if (
      isFromSockets ||
      (this._isSpectator && this._store.state.session.playerId !== player.id)
    )
      return;
    const index = this._store.state.players.players.indexOf(player);
    this._send("pronouns", [index, value]);
  }

  /**
   * Update a pronouns based on incoming data.
   * @param index
   * @param value
   * @private
   */
  _updatePlayerPronouns([index, value]) {
    const player = this._store.state.players.players[index];

    this._store.commit("players/update", {
      player,
      property: "pronouns",
      value,
      isFromSockets: true,
    });
  }

  /**
   * Handle incoming chat message
   * @param fromPlayerId sender's player ID
   * @param content message content
   * @param timestamp message timestamp
   * @private
   */
  _handleChatMessage({ fromPlayerId, content, timestamp }) {
    console.log("[Chat] Received message from:", fromPlayerId, "content:", content);
    const players = this._store.state.players.players;
    let senderName = "Unknown";

    if (fromPlayerId === "storyteller") {
      senderName = "Storyteller";
    } else {
      const player = players.find((p) => p.id === fromPlayerId);
      if (player) {
        const index = players.indexOf(player);
        const seatNumber = index + 1;
        const playerName =
          player.name === "__EMPTY_SEAT__" ? "Empty Seat" : player.name;
        senderName = `${seatNumber}. ${playerName}`;
      }
    }

    console.log("[Chat] Storing message from:", senderName);
    this._store.commit("session/receiveChatMessage", {
      fromPlayerId,
      senderName,
      content,
      timestamp,
    });
  }

  /**
   * Handle chat history response from server
   * @param chatHistories object with playerId -> messages array
   * @private
   */
  _handleChatHistory(chatHistories) {
    console.log("[Chat] Received chat histories:", chatHistories);
    const players = this._store.state.players.players;
    
    // Process each chat history
    for (let otherPlayerId in chatHistories) {
      const messages = chatHistories[otherPlayerId];
      console.log(`[Chat] Processing ${messages.length} messages for ${otherPlayerId}`);
      
      messages.forEach(({ fromPlayerId, content, timestamp }) => {
        let senderName = "Unknown";
        
        if (fromPlayerId === "storyteller") {
          senderName = "Storyteller";
        } else {
          const player = players.find((p) => p.id === fromPlayerId);
          if (player) {
            const index = players.indexOf(player);
            const seatNumber = index + 1;
            const playerName =
              player.name === "__EMPTY_SEAT__" ? "Empty Seat" : player.name;
            senderName = `${seatNumber}. ${playerName}`;
          }
        }
        
        this._store.commit("session/receiveChatMessage", {
          fromPlayerId,
          senderName,
          content,
          timestamp,
        });
      });
    }
  }

  /**
   * Request chat history from server
   */
  requestChatHistory() {
    console.log("[Chat] Requesting chat history from server");
    this._send("getChatHistory", this._store.state.session.playerId);
  }

  /**
   * Send a chat message to another player
   * @param targetPlayerId recipient's player ID
   * @param content message content
   */
  sendChatMessage({ targetPlayerId, content }) {
    console.log("[Chat] Sending message to:", targetPlayerId, "content:", content);
    this._send("chat", {
      targetPlayerId,
      content,
      timestamp: Date.now(),
    });
  }

  /**
   * Broadcast nickname update to all clients in the session
   * @param nickname new nickname
   */
  updateNickname(nickname) {
    // Use "host" as playerId if storyteller, otherwise use session playerId
    const playerId = this._isSpectator ? this._store.state.session.playerId : "host";
    console.log("[Nickname] Broadcasting update:", nickname, "for playerId:", playerId);
    this._send("updateNickname", {
      playerId,
      nickname,
    });
  }

  /**
   * Handle incoming nickname update from another client
   * @param playerId player ID whose nickname changed
   * @param nickname new nickname
   * @private
   */
  _handleNicknameUpdate({ playerId, nickname }) {
    console.log("[Nickname] Received update for playerId:", playerId, "new nickname:", nickname);
    const players = this._store.state.players.players;
    
    // Update all seats occupied by this player
    players.forEach((player) => {
      if (player.id === playerId) {
        console.log("[Nickname] Updating seat name to:", nickname || "__EMPTY_SEAT__");
        this._store.commit("players/update", {
          player,
          property: "name",
          value: nickname || "__EMPTY_SEAT__",
        });
      }
    });

    // Update chat messages for this player
    this._store.commit("session/updateChatSenderName", { playerId, nickname });

    // If this is the storyteller, update the storyteller fabled character
    if (playerId === "host") {
      console.log("[Nickname] Updating storyteller fabled character name to:", nickname);
      const fabled = this._store.state.players.fabled;
      const storytellerFabled = fabled.find((f) => f.isStoryteller);
      if (storytellerFabled) {
        const storytellerName = nickname || "Storyteller";
        this._store.commit("players/updateFabledName", {
          fabled: storytellerFabled,
          name: storytellerName,
        });
      }
    }
  }

  /**
   * Handle a ping message by another player / storyteller
   * @param playerIdOrCount
   * @param latency
   * @private
   */
  _handlePing([playerIdOrCount = 0, latency] = []) {
    const now = new Date().getTime();
    
    // Update heartbeat response time
    this._lastHeartbeatResponse = now;
    
    if (!this._isSpectator) {
      // remove players that haven't sent a ping in the timeout period (5 minutes)
      // This accommodates mobile screen sleep where JavaScript is paused
      for (let player in this._players) {
        if (now - this._players[player] > this._pingTimeout) {
          console.log("[Ping] Player timeout detected:", player, "last seen:", new Date(this._players[player]));
          delete this._players[player];
          delete this._pings[player];
        }
      }
      // remove claimed seats from players that are no longer connected
      // but skip this check if ST just connected (give players time to reconnect)
      if (!this._justConnected) {
        this._store.state.players.players.forEach((player) => {
          if (player.id && !this._players[player.id]) {
            this._store.commit("players/update", {
              player,
              property: "id",
              value: "",
            });
          }
        });
      } else {
        // After first ping cycle, clear the flag
        this._justConnected = false;
      }
      // store new player data
      if (playerIdOrCount) {
        this._players[playerIdOrCount] = now;
        const ping = parseInt(latency, 10);
        if (ping && ping > 0 && ping < 30 * 1000) {
          // ping to Players
          this._pings[playerIdOrCount] = ping;
          const pings = Object.values(this._pings);
          this._store.commit(
            "session/setPing",
            Math.round(pings.reduce((a, b) => a + b, 0) / pings.length),
          );
        }
      }
    } else if (latency) {
      // ping to ST
      this._store.commit("session/setPing", parseInt(latency, 10));
    }
    // update player count
    if (!this._isSpectator || playerIdOrCount) {
      this._store.commit(
        "session/setPlayerCount",
        this._isSpectator ? playerIdOrCount : Object.keys(this._players).length,
      );
    }
  }

  /**
   * Handle a player leaving the sessions. ST only
   * @param playerId
   * @private
   */
  _handleBye(playerId) {
    if (this._isSpectator) return;
    delete this._players[playerId];
    
    // Clear the seat occupied by this player
    const players = this._store.state.players.players;
    const playerIndex = players.findIndex(({ id }) => id === playerId);
    if (playerIndex >= 0) {
      console.log(`[WebSocket] Player ${playerId} left, clearing seat ${playerIndex}`);
      // Clear player ID
      this._store.commit("players/update", {
        player: players[playerIndex],
        property: "id",
        value: "",
      });
      // Reset name to empty seat marker
      this._store.commit("players/update", {
        player: players[playerIndex],
        property: "name",
        value: "__EMPTY_SEAT__",
      });
    }
    
    this._store.commit(
      "session/setPlayerCount",
      Object.keys(this._players).length,
    );
  }

  /**
   * Claim a seat, needs to be confirmed by the Storyteller.
   * Seats already occupied can't be claimed.
   * @param seat either -1 to vacate or the index of the seat claimed
   */
  claimSeat(seat) {
    if (!this._isSpectator) return;
    const players = this._store.state.players.players;
    if (players.length > seat && (seat < 0 || !players[seat].id)) {
      const nickname = this._store.state.grimoire.nickname || "";
      this._send("claim", [seat, this._store.state.session.playerId, nickname]);
    }
  }

  /**
   * Update a player id associated with that seat.
   * @param index seat index or -1
   * @param value playerId to add / remove
   * @param nickname player's nickname (optional)
   * @private
   */
  _updateSeat([index, value, nickname]) {
    if (this._isSpectator) return;
    const property = "id";
    const players = this._store.state.players.players;
    // remove previous seat
    const oldIndex = players.findIndex(({ id }) => id === value);
    if (oldIndex >= 0 && oldIndex !== index) {
      this._store.commit("players/update", {
        player: players[oldIndex],
        property,
        value: "",
      });
      // reset name to special marker when vacating
      this._store.commit("players/update", {
        player: players[oldIndex],
        property: "name",
        value: "__EMPTY_SEAT__",
      });
    }
    // add playerId to new seat
    if (index >= 0) {
      const player = players[index];
      if (!player) return;
      this._store.commit("players/update", { player, property, value });
      // update player name with nickname if provided
      if (nickname && nickname.trim()) {
        this._store.commit("players/update", {
          player,
          property: "name",
          value: nickname.trim(),
        });
      }
    }
    // update player session list as if this was a ping
    this._handlePing([true, value, 0]);
  }

  /**
   * Distribute player roles to all seated players in a direct message.
   * This will be split server side so that each player only receives their own (sub)message.
   */
  distributeRoles() {
    if (this._isSpectator) return;
    const message = {};
    this._store.state.players.players.forEach((player, index) => {
      if (player.id && player.role) {
        // Keep null for empty role selections (player.role.id || null)
        message[player.id] = [
          "player",
          { index, property: "role", value: player.role.id || null },
        ];
      }
    });
    if (Object.keys(message).length) {
      this._send("direct", message);
    }
  }

  /**
   * A player nomination. ST only
   * This also syncs the voting speed to the players.
   * Payload can be an object with {nomination} property or just the nomination itself, or undefined.
   * @param payload [nominator, nominee]|{nomination}
   */
  nomination(payload) {
    if (this._isSpectator) return;
    const nomination = payload ? payload.nomination || payload : payload;
    const players = this._store.state.players.players;
    if (
      !nomination ||
      (players.length > nomination[0] && players.length > nomination[1])
    ) {
      this.setVotingSpeed(this._store.state.session.votingSpeed);
      this._send("nomination", nomination);
    }
  }

  /**
   * Set the isVoteInProgress status. ST only
   */
  setVoteInProgress() {
    if (this._isSpectator) return;
    this._send("isVoteInProgress", this._store.state.session.isVoteInProgress);
  }

  /**
   * Send the isNight status. ST only
   */
  setIsNight() {
    if (this._isSpectator) return;
    this._send("isNight", this._store.state.grimoire.isNight);
  }

  /**
   * Send the isVoteHistoryAllowed state. ST only
   */
  setVoteHistoryAllowed() {
    if (this._isSpectator) return;
    this._send(
      "isVoteHistoryAllowed",
      this._store.state.session.isVoteHistoryAllowed,
    );
  }

  /**
   * Send the voting speed. ST only
   * @param votingSpeed voting speed in seconds, minimum 1
   */
  setVotingSpeed(votingSpeed) {
    if (this._isSpectator) return;
    if (votingSpeed) {
      this._send("votingSpeed", votingSpeed);
    }
  }

  /**
   * Set which player is on the block. ST only
   * @param playerIndex, player id or -1 for empty
   */
  setMarked(playerIndex) {
    if (this._isSpectator) return;
    this._send("marked", playerIndex);
  }

  /**
   * Clear the vote history for everyone. ST only
   */
  clearVoteHistory() {
    if (this._isSpectator) return;
    this._send("clearVoteHistory");
  }

  /**
   * Send a vote. Player or ST
   * @param index Seat of the player
   * @param sync Flag whether to sync this vote with others or not
   */
  vote([index]) {
    const player = this._store.state.players.players[index];
    if (
      this._store.state.session.playerId === player.id ||
      !this._isSpectator
    ) {
      // send vote only if it is your own vote or you are the storyteller
      this._send("vote", [
        index,
        this._store.state.session.votes[index],
        !this._isSpectator,
      ]);
    }
  }

  /**
   * Handle an incoming vote, but only if it is from ST or unlocked.
   * @param index
   * @param vote
   * @param fromST
   */
  _handleVote([index, vote, fromST]) {
    const { session, players } = this._store.state;
    const playerCount = players.players.length;
    const indexAdjusted =
      (index - 1 + playerCount - session.nomination[1]) % playerCount;
    if (fromST || indexAdjusted >= session.lockedVote - 1) {
      this._store.commit("session/vote", [index, vote]);
    }
  }

  /**
   * Lock a vote. ST only
   */
  lockVote() {
    if (this._isSpectator) return;
    const { lockedVote, votes, nomination } = this._store.state.session;
    const { players } = this._store.state.players;
    const index = (nomination[1] + lockedVote - 1) % players.length;
    this._send("lock", [this._store.state.session.lockedVote, votes[index]]);
  }

  /**
   * Update vote lock and the locked vote, if it differs. Player only
   * @param lock
   * @param vote
   * @private
   */
  _handleLock([lock, vote]) {
    if (!this._isSpectator) return;
    this._store.commit("session/lockVote", lock);
    if (lock > 1) {
      const { lockedVote, nomination } = this._store.state.session;
      const { players } = this._store.state.players;
      const index = (nomination[1] + lockedVote - 1) % players.length;
      if (this._store.state.session.votes[index] !== vote) {
        this._store.commit("session/vote", [index, vote]);
      }
    }
  }

  /**
   * Swap two player seats. ST only
   * @param payload
   */
  swapPlayer(payload) {
    if (this._isSpectator) return;
    this._send("swap", payload);
  }

  /**
   * Move a player to another seat. ST only
   * @param payload
   */
  movePlayer(payload) {
    if (this._isSpectator) return;
    this._send("move", payload);
  }

  /**
   * Remove a player. ST only
   * @param payload
   */
  removePlayer(payload) {
    if (this._isSpectator) return;
    this._send("remove", payload);
  }

  /**
   * Send bluff roles to a specific player. ST only
   * @param playerId target player ID
   */
  sendBluffs(playerId) {
    if (this._isSpectator) return;
    
    // Get the bluffs that storyteller has selected in the grimoire (left-bottom corner)
    const bluffs = this._store.state.players.bluffs;
    
    console.log("[Bluffs] Sending storyteller's selected bluffs to player:", playerId, "bluffs:", bluffs);
    
    // Send bluffs as a direct message to the specific player
    // Keep null for empty selections (role.id || null instead of role.id)
    const message = {
      [playerId]: ["bluffs", bluffs.map(role => role.id || null)]
    };
    this._send("direct", message);
  }

  /**
   * Handle received bluffs from storyteller
   * @param roleIds array of role IDs
   * @private
   */
  _handleBluffs(roleIds) {
    if (!this._isSpectator) return;
    
    console.log("[Bluffs] Received bluffs:", roleIds);
    
    // Get full role objects from IDs (roles is a Map)
    // Keep null values to support empty/blank selections
    const roles = this._store.state.roles;
    const bluffRoles = roleIds.map(id => id ? roles.get(id) : null);
    
    // Store bluffs in player's state to display in left-bottom corner
    bluffRoles.forEach((role, index) => {
      this._store.commit("players/setBluff", { index, role: role || {} });
    });
    
    console.log("[Bluffs] Bluffs saved to player's grimoire:", bluffRoles);
  }

  /**
   * Handle received screenshot from storyteller
   * @param data { msgId, imageData, mode, ttl, timestamp }
   * @private
   */
  _handleScreenshot(data) {
    if (!this._isSpectator) return;
    console.log("[Screenshot] Received screenshot:", data.msgId, "mode:", data.mode);
    this._store.commit("session/receiveScreenshot", {
      fromPlayerId: "storyteller",
      msgId: data.msgId,
      imageData: data.imageData,
      mode: data.mode,
      ttl: data.ttl,
      timestamp: data.timestamp,
    });
  }

  /**
   * Handle screenshot revoked by storyteller
   * @param data { msgId }
   * @private
   */
  _handleScreenshotRevoked(data) {
    if (!this._isSpectator) return;
    console.log("[Screenshot] Screenshot revoked:", data.msgId);
    this._store.commit("session/handleScreenshotRevoked", { msgId: data.msgId });
  }

  /**
   * Send a screenshot to a player. ST only
   * @param targetPlayerId
   * @param msgId
   * @param imageData
   * @param mode
   * @param ttl
   */
  sendScreenshot({ targetPlayerId, msgId, imageData, mode, ttl }) {
    if (this._isSpectator) return;
    console.log("[Screenshot] Sending screenshot to:", targetPlayerId, "msgId:", msgId);
    this._send("screenshot", {
      targetPlayerId,
      msgId,
      imageData,
      mode,
      ttl,
      timestamp: Date.now(),
    });
  }

  /**
   * Notify server that player viewed a screenshot
   * @param msgId
   */
  sendScreenshotViewed(msgId) {
    console.log("[Screenshot] Sending viewed event for:", msgId);
    this._send("screenshotViewed", { msgId });
  }

  /**
   * Revoke a screenshot. ST only
   * @param targetPlayerId
   * @param msgId
   */
  sendRevokeScreenshot({ targetPlayerId, msgId }) {
    if (this._isSpectator) return;
    console.log("[Screenshot] Revoking screenshot:", msgId, "for player:", targetPlayerId);
    this._send("revokeScreenshot", { targetPlayerId, msgId });
  }
}

export default (store) => {
  // setup
  const session = new LiveSession(store);

  // listen to mutations
  store.subscribe(({ type, payload }, state) => {
    switch (type) {
      case "session/setSessionId":
        if (state.session.sessionId) {
          session.connect(state.session.sessionId);
        } else {
          window.location.hash = "";
          session.disconnect();
        }
        break;
      case "session/claimSeat":
        session.claimSeat(payload);
        break;
      case "session/distributeRoles":
        if (payload) {
          session.distributeRoles();
        }
        break;
      case "session/nomination":
      case "session/setNomination":
        session.nomination(payload);
        break;
      case "session/setVoteInProgress":
        session.setVoteInProgress(payload);
        break;
      case "session/voteSync":
        session.vote(payload);
        break;
      case "session/lockVote":
        session.lockVote();
        break;
      case "session/setVotingSpeed":
        session.setVotingSpeed(payload);
        break;
      case "session/clearVoteHistory":
        session.clearVoteHistory();
        break;
      case "session/setVoteHistoryAllowed":
        session.setVoteHistoryAllowed();
        break;
      case "session/sendChatMessage":
        session.sendChatMessage(payload);
        break;
      case "session/updateNickname":
        session.updateNickname(payload);
        break;
      case "session/sendBluffsToPlayer":
        session.sendBluffs(payload);
        break;
      case "session/sendScreenshotMessage":
        session.sendScreenshot(payload);
        break;
      case "session/screenshotViewed":
        session.sendScreenshotViewed(payload.msgId);
        break;
      case "session/revokeScreenshot":
        session.sendRevokeScreenshot(payload);
        break;
      case "toggleNight":
        session.setIsNight();
        break;
      case "setEdition":
        session.sendEdition();
        break;
      case "players/setFabled":
        session.sendFabled();
        break;
      case "session/setMarkedPlayer":
        session.setMarked(payload);
        break;
      case "players/swap":
        session.swapPlayer(payload);
        break;
      case "players/move":
        session.movePlayer(payload);
        break;
      case "players/remove":
        session.removePlayer(payload);
        break;
      case "triggerClearRolesSync":
        // Send clearRoles command to sync role clearing to all players
        session.sendClearRoles();
        break;
      case "players/set":
      case "players/clear":
      case "players/add":
        session.sendGamestate("", true);
        break;
      case "players/update":
        if (payload.property === "pronouns") {
          session.sendPlayerPronouns(payload);
        } else {
          session.sendPlayer(payload);
        }
        break;
    }
  });

  // check for session Id in hash
  const sessionId = window.location.hash.substr(1);
  if (sessionId) {
    store.commit("session/setSpectator", true);
    store.commit("session/setSessionId", sessionId);
    store.commit("toggleGrimoire", false);
  }
};
